import {
  CorrectInstructorCheckInDialog,
  type InstructorCheckInAttendee,
} from "@/modules/classes/presentation/ui/CorrectInstructorCheckInDialog";
import {
  usePatchInstructorCheckInTimeMutation,
} from "@/modules/attendance/presentation/api/queries";
import {
  EditCheckInTimeDialog,
  type EditCheckInTimeTarget,
} from "@/modules/attendance/presentation/ui/EditCheckInTimeDialog";
import { formatDateTime } from "@/shared/lib/date-time";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@devhop/ui";
import { ClockIcon, PencilIcon, UserCheckIcon } from "lucide-react";
import { useMemo, useState } from "react";
import {
  useCorrectInstructorCheckInBooking,
  usePrivateLessonBookingInstructorAttendeesQuery,
} from "../api/queries";

type PrivateLessonInstructorAttendanceCardProps = {
  bookingId: string;
  instructorIds: string[];
  instructorNames: string[];
  status: "scheduled" | "completed" | "cancelled";
  canCorrect?: boolean;
  onCorrected?: () => void;
};

export function PrivateLessonInstructorAttendanceCard({
  bookingId,
  instructorIds,
  instructorNames,
  status,
  canCorrect = false,
  onCorrected,
}: PrivateLessonInstructorAttendanceCardProps) {
  const { data, isLoading } =
    usePrivateLessonBookingInstructorAttendeesQuery(bookingId);
  const correctMutation = useCorrectInstructorCheckInBooking(bookingId);
  const patchTimeMutation = usePatchInstructorCheckInTimeMutation({
    onSuccess: onCorrected,
  });
  const attendees = data?.attendees ?? [];

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTimeOpen, setEditTimeOpen] = useState(false);
  const [selectedAttendee, setSelectedAttendee] =
    useState<InstructorCheckInAttendee | null>(null);
  const [editTimeTarget, setEditTimeTarget] =
    useState<EditCheckInTimeTarget | null>(null);

  const correctOptions = useMemo(() => {
    const checkedInIds = new Set(attendees.map((a) => a.instructorId));
    return instructorIds
      .map((id, index) => ({
        id,
        name: instructorNames[index] ?? id,
      }))
      .filter((inst) => !checkedInIds.has(inst.id));
  }, [attendees, instructorIds, instructorNames]);

  const canShowCorrect =
    canCorrect &&
    status !== "cancelled" &&
    attendees.length > 0 &&
    correctOptions.length > 0;

  const canEditTime =
    canCorrect && status !== "cancelled" && attendees.length > 0;

  const isSubmitting =
    correctMutation.isPending || patchTimeMutation.isPending;

  const handleOpenCorrect = (attendee: InstructorCheckInAttendee) => {
    setSelectedAttendee(attendee);
    setDialogOpen(true);
  };

  const handleOpenEditTime = (attendee: InstructorCheckInAttendee) => {
    setEditTimeTarget({
      id: attendee.id,
      label: attendee.instructorName,
      checkedInAt: attendee.checkedInAt,
    });
    setEditTimeOpen(true);
  };

  const handleConfirmEditTime = async (values: {
    id: string;
    checkedInAt: string;
  }) => {
    await patchTimeMutation.mutateAsync({
      checkInId: values.id,
      checkedInAt: values.checkedInAt,
    });
    setEditTimeOpen(false);
    setEditTimeTarget(null);
  };

  const handleConfirm = async (values: {
    wrongCheckInId: string;
    correctInstructorId: string;
    reason: string;
  }) => {
    await correctMutation.mutateAsync(values);
    setDialogOpen(false);
    setSelectedAttendee(null);
    onCorrected?.();
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="flex items-center gap-2">
              <UserCheckIcon className="size-4" />
              ການກວດຊື່ຄູສອນ
            </CardTitle>
            <Badge variant="secondary">{attendees.length} ຄົນ</Badge>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດ...</p>
          ) : attendees.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              ຍັງບໍ່ມີການກວດຊື່ຄູສອນ
            </p>
          ) : (
            <ul className="space-y-2">
              {attendees.map((attendee) => (
                <li
                  key={attendee.id}
                  className="flex items-center justify-between gap-2 rounded-md border px-3 py-2"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium text-sm">
                        {attendee.instructorName}
                      </p>
                      {attendee.isSubstitute && (
                        <Badge
                          variant="outline"
                          className="border-amber-500/40 text-amber-700"
                        >
                          ສອນແທນ
                        </Badge>
                      )}
                    </div>
                    <p className="text-muted-foreground text-xs">
                      {formatDateTime(attendee.checkedInAt)}
                    </p>
                  </div>
                  {(canShowCorrect || canEditTime) && (
                    <div className="flex shrink-0 gap-1">
                      {canEditTime && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="gap-1.5"
                          disabled={isSubmitting}
                          onClick={() => handleOpenEditTime(attendee)}
                        >
                          <ClockIcon className="size-3.5" />
                          ເວລາ
                        </Button>
                      )}
                      {canShowCorrect && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="gap-1.5"
                          disabled={isSubmitting}
                          onClick={() => handleOpenCorrect(attendee)}
                        >
                          <PencilIcon className="size-3.5" />
                          ແກ້ໄຂ
                        </Button>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <CorrectInstructorCheckInDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        wrongAttendee={selectedAttendee}
        correctOptions={correctOptions}
        onConfirm={handleConfirm}
        isSubmitting={correctMutation.isPending}
      />

      <EditCheckInTimeDialog
        open={editTimeOpen}
        onOpenChange={setEditTimeOpen}
        target={editTimeTarget}
        onConfirm={handleConfirmEditTime}
        isSubmitting={patchTimeMutation.isPending}
      />
    </>
  );
}
